/**
 * User Detail — one user, the tenant they belong to, and the one action an
 * operator opens this page for.
 *
 * There was no such page. Every route from "a customer says their account is
 * wrong" to "I am looking at their account" went through a list row and a
 * literal reason. This is the minimum: identity as the platform users API
 * returns it (the same allowlisted shape as the list — never the raw
 * document), the tenant as a link, and Login as User through the shared
 * reason dialog and `startViewAs`. Nothing here builds a CRM URL.
 *
 * Visual language follows Tenant Detail: a back link, a name with its status
 * beside it, flat monochrome actions. No identity glyph — this page has no
 * avatar to put one on, and a decorative one would be artwork.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Loader2, LogIn, Mail, Shield } from 'lucide-react';

import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import StatusBadge from '../../../components/admin/StatusBadge';
import ImpersonationReasonDialog from '../tenants/ImpersonationReasonDialog';
import { describeUserStatus, describeUserSetup } from './userPresentation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;
const CONTROL_PLANE_API = `${BACKEND_URL}/api/admin/control-plane`;

/*
 * STATUS IS THE LIST'S VOCABULARY, NOT A THIRD ONE. This page opened with its
 * own three-entry tone map in raw palette swatches (green-100 under
 * green-700, and so on) — a colour that does not move with the theme, on the
 * page an operator reaches FROM a list that already draws the same status
 * through `describeUserStatus` and `StatusBadge`. One status, one rendering;
 * and a status this build does not know is shown as itself, muted, rather
 * than defaulting to "Active" as the old map did.
 */

/**
 * Why Login as User is refused for this user, or null when it is offered.
 * Only the states the users API can report; a status it does not name is not
 * silently treated as active.
 */
export const loginRefusal = (user) => {
  if (!user) return null;
  if (user.status === 'disabled' || user.is_active === false) {
    return 'This user is deactivated. Reactivate them from the tenant before opening a session.';
  }
  if (user.status === 'invited') {
    return 'This user has not accepted their invitation yet, so there is no workspace session to open.';
  }
  return null;
};

const fmt = (v) => (v ? new Date(v).toLocaleString() : null);

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getAdminToken } = useAdminAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewAsTarget, setViewAsTarget] = useState(null);

  const headers = { Authorization: `Bearer ${getAdminToken()}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${ADMIN_API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      setUser(data);
    } catch (e) {
      setUser(null);
      setError(e?.response?.status === 404
        ? 'No user with this id is visible to the Admin Portal.'
        : 'This user could not be loaded.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the id changes
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const refusal = loginRefusal(user);
  const fullName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : '';
  const status = user ? describeUserStatus(user.status) : null;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="user-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading user" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="user-detail-error">
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')} className="mb-4 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Users
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {error || 'This user could not be loaded.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="user-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/users')}
          className="mb-4 text-muted-foreground hover:text-foreground"
          data-testid="back-to-users"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Users
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground truncate" data-testid="user-name">
                {fullName || user.email}
              </h1>
              <span data-testid="user-status">
                <StatusBadge status={status.tone} label={status.label} />
              </span>
              {describeUserSetup(user) && (
                <span className="text-xs text-muted-foreground" data-testid="user-setup-state">
                  {describeUserSetup(user)}
                </span>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-muted-foreground" data-testid="user-email">
              <Mail className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {user.email}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              <Button
                variant="link"
                className="h-auto p-0 text-primary hover:text-primary/80"
                onClick={() => navigate(`/tenants/${user.tenant_id}`)}
                data-testid="user-tenant-link"
              >
                <Building2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                {user.tenant_name || user.tenant_id}
              </Button>
              {user.tenant_subdomain ? (
                <span className="ml-2 font-mono text-xs text-muted-foreground">{user.tenant_subdomain}</span>
              ) : null}
            </p>
          </div>

          {/* THE action. Gated on tenant:impersonate — hidden rather than
              disabled, because an operator who cannot open sessions has no
              use for knowing the page could. Refused with a sentence for a
              user who has no session to open. */}
          <div className="flex items-center gap-2">
            <PermissionGate permission="tenant:impersonate" mode="hide">
              <Button
                size="sm"
                disabled={!!refusal}
                title={refusal || 'Open the CRM as this user, in a new tab'}
                aria-describedby={refusal ? 'login-as-user-refusal' : undefined}
                onClick={() => setViewAsTarget({
                  tenantId: user.tenant_id,
                  tenantName: user.tenant_name,
                  userId: user.id,
                  userEmail: user.email,
                  userName: fullName || undefined,
                })}
                data-testid="login-as-user"
              >
                <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
                Login as User
              </Button>
            </PermissionGate>
          </div>
        </div>
        {refusal && (
          <p id="login-as-user-refusal" className="mt-2 text-sm text-muted-foreground" data-testid="login-as-user-refusal">{refusal}</p>
        )}
      </div>

      {/* Identity */}
      <Card>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-base font-semibold">Identity</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Email</dt>
              <dd className="text-sm text-foreground break-all">{user.email}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground">{fullName || <span className="text-muted-foreground">Not set</span>}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Tenant</dt>
              <dd className="text-sm text-foreground">{user.tenant_name || user.tenant_id}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Status</dt>
              <dd className="text-sm text-foreground">{status.label}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Role</dt>
              <dd className="text-sm text-foreground" data-testid="user-role">
                <span className="flex flex-wrap items-center gap-1.5">
                  {/* Capability first, label second — the same rule as the
                      list. A correctly provisioned System Administrator
                      carries no hierarchy role at all. */}
                  {user.is_system_administrator && (
                    <Badge variant="brand" className="flex items-center gap-1">
                      <Shield className="h-3 w-3" aria-hidden="true" />
                      System Administrator
                    </Badge>
                  )}
                  {user.role_name || user.role ? (
                    <Badge variant="outline" className="font-normal capitalize">
                      {user.role_name || user.role}
                    </Badge>
                  ) : (
                    !user.is_system_administrator && <span className="text-xs text-muted-foreground">No label</span>
                  )}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Last login</dt>
              <dd className="text-sm text-foreground">{fmt(user.last_login) || <span className="text-muted-foreground">Never</span>}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground">{fmt(user.created_at) || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">User id</dt>
              <dd className="font-mono text-xs text-muted-foreground break-all">{user.id}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <ImpersonationReasonDialog
        target={viewAsTarget}
        post={(path, body) => axios.post(`${CONTROL_PLANE_API}${path}`, body, { headers })}
        onClose={() => setViewAsTarget(null)}
      />
    </div>
  );
};

export default UserDetailPage;
